import { useEffect, useState } from 'react';
import type { SrdSearchIndex } from '@roll1/shared/srd/types';

/**
 * The SRD names for the search (GET /api/srd/index — reference data, the same for every caller, no campaign data).
 * Fetched once per page load, on the first need; a failed fetch is retried on the next need.
 */
let cached: SrdSearchIndex | null = null;
let inflight: Promise<SrdSearchIndex | null> | null = null;

export function loadSrdIndex(): Promise<SrdSearchIndex | null> {
  if (cached !== null) return Promise.resolve(cached);
  inflight ??= fetch('/api/srd/index')
    .then((r) => (r.ok ? (r.json() as Promise<SrdSearchIndex>) : null))
    .then((index) => {
      cached = index;
      return index;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** The index once `wanted` has been true (the search opened); null until it arrives. */
export function useSrdIndex(wanted: boolean): SrdSearchIndex | null {
  const [index, setIndex] = useState<SrdSearchIndex | null>(cached);
  useEffect(() => {
    if (!wanted || index !== null) return;
    let alive = true;
    void loadSrdIndex().then((loaded) => {
      if (alive && loaded !== null) setIndex(loaded);
    });
    return () => {
      alive = false;
    };
  }, [wanted, index]);
  return index;
}
